import { lazy } from 'react';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import type { ModuleManifest } from '../../lib/host/types';
import { templatesKeys } from './queries/keys';

// Templates module — the template LIBRARY page (P04 §4; story 4-2a). The
// route stays `/app/report-presets` (no rename, no new redirect — every
// bookmark and e2e spec already asserts it; the OnboardingTour route-pin that
// once also pointed here is retired, story 2-106); what moves
// is OWNERSHIP: the nav row that used to be hard-coded in
// `lib/registries/index.ts` now lives on this manifest (DOD §1.8 "registries,
// not shell edits" — `src/App.tsx` no longer declares the route either, see
// its comment at the old call site).
//
// WHAT IS DELIBERATELY *NOT* IN THIS FOLDER: `TemplateEditorPage` and
// `TemplatePublishBar`. Both stay under `src/pages` / `src/components` this
// story — the editor is full-screen shell code (`chrome: 'full'` territory,
// though it isn't even module-routed today) consumed from the create/import
// entry points on THIS page, and the shell may not import a module (the
// pluggability test, `no-restricted-imports`). Folding the editor in here
// would make every entry point into it a cross-module import from the pages
// that still need it (TemplateEditorPage itself, plus anything that deep-links
// into it). 4-2b/4-3 is where the editor's own home gets revisited.
//
// NO `entities` / `search` (G-21, DOD §4). Story 3-7 owns template
// search-indexing — see the header comment in
// `backend/src/services/searchIndex.service.js` line 5: "subjects/
// inspections/templates join in 3-7". Declaring `entities: ['template']` here
// with no matching `search[]` row would fail the host-boot assertion
// (`assertModuleSearchCoverage`); declaring it WITH a row would be this story
// reaching into 3-7's scope. Omitting both, same as the `inspectionTypes`
// module does for the identical reason, is the honest state until 3-7 lands.
export const manifest: ModuleManifest = {
  key: 'templates',
  featureKeys: ['templates.editor'],
  nav: [
    {
      key: 'templates',
      slot: 'main',
      label: 'Templates',
      to: '/app/report-presets',
      icon: DocumentDuplicateIcon,
      // Read for `templates:read` — every staff role that must satisfy a
      // template (an inspector checking what a linked type requires, a
      // reviewer browsing what is out for review) holds it; the mutating
      // affordances on the page itself (New template, Set default, Delete)
      // gate separately on `templates:create` / `templates:delete`
      // (`backend/src/routes/v1/reportPreset.route.js`), same read/write split
      // `inspectionTypes` draws for `types:read` vs `types:manage`.
      permission: 'templates:read',
      featureKey: 'templates.editor',
      order: 50,
    },
  ],
  routes: [
    {
      path: '/app/report-presets',
      element: lazy(() =>
        import('./pages/TemplateLibraryPage').then((m) => ({ default: m.TemplateLibraryPage })),
      ),
      chrome: 'shell',
      // Same gate as the nav row above (story 2-20's route ↔ row alignment).
      permission: 'templates:read',
      // P16 §6.1 collection family (story 2-146a).
      layoutFamily: 'collection',
    },
  ],
  // P09 §2.1 parity. The lifecycle verbs shipped with stories 4-1a/4-1b and the
  // report-preset CRUD predates the module system entirely; story 4-2c adds the
  // three entry-path tools at the bottom, each with its endpoint in the same
  // story (DOD §1.3). Naming them here is the module declaring which gateway
  // tools belong to it, matching what `agents/src/capabilities/presets.js` and
  // `templates.js` actually export.
  agentTools: [
    'preset_list',
    'preset_get',
    'preset_commit_draft',
    'template_lifecycle_get',
    'template_version_list',
    'template_version_get',
    'template_publish',
    'template_archive',
    'template_version_restore',
    'template_submit',
    'template_approve',
    'template_reject',
    // ── Story 4-2c: the four entry paths (P04 §7) ──────────────────────
    'template_starters_list',
    'template_draft_preview',
    'template_create',
  ],
  queryKeys: templatesKeys,
};
